import styled from "styled-components";

export const Wrapper = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.light,
  flexGrow: 1,
  paddingBottom: 50,
  minHeight: "100vh",

  "& .orders-container": {
    backgroundColor: theme.palette.white,
    borderRadius: 8,
    boxShadow: `0 1px 3px ${theme.palette.shadow.medium}`,
    border: `1px solid ${theme.palette.border.light}`,
    overflow: "hidden",
    width: "100%",
    boxSizing: "border-box",
  },

  "& .desktop-view": {
    display: "block",
    width: "100%",
  },

  "& .mobile-view": {
    display: "none",
  },

  [theme.down("md")]: {
    "& .desktop-view": {
      display: "none",
    },

    "& .mobile-view": {
      display: "block",
    },
  },
}));

export const HeaderWithSubtitle = styled("div")(({ theme }) => ({
  ...theme.paddings(20, 0, 15),
}));

export const TitleComponent = styled("div")(({ theme }) => ({
  fontSize: 32,
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: 8,

  [theme.down("sm")]: {
    fontSize: 24,
  },
}));

export const Subtitle = styled("div")(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.text.secondary,
  fontWeight: 400,

  [theme.down("sm")]: {
    fontSize: 14,
  },
}));

export const NotFoundComponent = styled("div")(({ theme }) => ({
  ...theme.mixins.flexCenterCenter,
  minHeight: 200,
  textAlign: "center",
  fontSize: 18,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.white,
  borderRadius: 8,
  marginTop: 20,
  padding: 40,
}));

export const StatusSummaryCards = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 20,
  marginTop: 24,
  marginBottom: 24,

  [theme.down("md")]: {
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 12,
  },

  [theme.down("sm")]: {
    gridTemplateColumns: "1fr",
    gap: 12,
  },
}));

export const StatusCard = styled("div")(({ theme, variant }) => {
  const variants = {
    success: {
      background: `linear-gradient(135deg, ${theme.palette.success?.light || '#e8f5e9'} 0%, ${theme.palette.success?.lighter || '#f1f8f4'} 100%)`,
      border: `1px solid ${theme.palette.success?.border || '#c8e6c9'}`,
    },
    cancelled: {
      background: `linear-gradient(135deg, ${theme.palette.error?.light || '#ffebee'} 0%, ${theme.palette.error?.lighter || '#fce4e4'} 100%)`,
      border: `1px solid ${theme.palette.error?.border || '#ffcdd2'}`,
    },
    timeout: {
      background: `linear-gradient(135deg, ${theme.palette.warning?.light || '#fff3e0'} 0%, ${theme.palette.warning?.lighter || '#fff8e1'} 100%)`,
      border: `1px solid ${theme.palette.warning?.border || '#ffe0b2'}`,
    },
  };

  return {
    backgroundColor: theme.palette.white,
    padding: 24,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    gap: 16,
    boxShadow: `0 2px 8px ${theme.palette.shadow.medium}`,
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    cursor: "default",
    ...variants[variant],

    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 4px 12px ${theme.palette.shadow.medium}`,
    },

    [theme.down("md")]: {
      padding: 16,
      gap: 12,
    },
  };
});

export const StatusCardIcon = styled("div")(({ theme, variant }) => {
  const variants = {
    success: {
      background: theme.palette.success?.main || "#4caf50",
    },
    cancelled: {
      background: theme.palette.error?.main || "#f44336",
    },
    timeout: {
      background: theme.palette.warning?.main || "#ff9800",
    },
  };

  return {
    width: 56,
    height: 56,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    color: theme.palette.white,
    flexShrink: 0,
    ...variants[variant],

    [theme.down("md")]: {
      width: 48,
      height: 48,
      fontSize: 24,
    },
  };
});

export const StatusCardContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 4,
}));

export const StatusCardNumber = styled("div")(({ theme }) => ({
  fontSize: 32,
  fontWeight: 700,
  color: theme.palette.text.primary,
  lineHeight: 1,

  [theme.down("md")]: {
    fontSize: 28,
  },
}));

export const StatusCardLabel = styled("div")(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.text.secondary,
  textTransform: "capitalize",

  [theme.down("md")]: {
    fontSize: 13,
  },
}));

export const StatusBadge = styled("div")(({ theme, color }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  padding: "6px 12px",
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 500,
  backgroundColor: color || theme.palette.border.light,
  color: theme.palette.white,
  whiteSpace: "nowrap",
  maxWidth: "100%",
  textAlign: "center",

  "& .icon": {
    display: "flex",
    alignItems: "center",
    fontSize: 16,
    flexShrink: 0,
  },
}));

export const MobileOrdersContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 16,
}));

export const MobileOrderCard = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.white,
  borderRadius: 12,
  padding: 20,
  boxShadow: `0 2px 8px ${theme.palette.shadow.medium}`,
  cursor: "pointer",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",

  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 4px 12px ${theme.palette.shadow.medium}`,
  },

  "&:active": {
    transform: "translateY(0)",
  },
}));

export const MobileOrderCardHeader = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: 16,
  paddingBottom: 16,
  borderBottom: `1px solid ${theme.palette.border.light}`,
  gap: 12,

  "& > div:first-child": {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.text.primary,
    flex: 1,

    "& svg": {
      flexShrink: 0,
    },
  },
}));

export const MobileOrderCardRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: "8px 0",
  gap: 12,
}));

export const MobileOrderCardLabel = styled("div")(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.secondary,
  fontWeight: 500,
  minWidth: "40%",
}));

export const MobileOrderCardValue = styled("div")(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.primary,
  fontWeight: 400,
  textAlign: "right",
  flex: 1,

  "&.price": {
    fontWeight: 600,
    fontSize: 15,
  },

  "& > div": {
    lineHeight: 1.4,
  },
}));

export const OrdersBlock = styled("div")(({ theme }) => ({
  ...theme.mixins.flexColumn,
  flexGrow: 1,
  gap: 3,
  paddingTop: 20,
  minWidth: 1370,

  [theme.down("md")]: {
    width: "100%",
  },
}));

export const OrdersHeader = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 2fr 1fr 2fr 1fr 2fr 1fr 1fr",
  gap: 10,
  padding: "20px 10px",
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.white,
}));

export const PaginationWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 8,
  marginTop: 32,
  padding: "20px 0",
}));

export const PaginationButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: 8,
  border: `1px solid ${theme.palette.border.light}`,
  backgroundColor: theme.palette.white,
  color: theme.palette.text.primary,
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  transition: "all 0.2s ease",

  "&:hover:not(:disabled)": {
    backgroundColor: theme.palette.background.light,
    borderColor: theme.palette.border.darker,
  },

  "&:active:not(:disabled)": {
    transform: "scale(0.95)",
  },

  "&:disabled": {
    opacity: 0.4,
    cursor: "not-allowed",
  },

  "&.active": {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.white,
    borderColor: theme.palette.secondary.main,
  },
}));

export const PageNumber = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 4,
  fontSize: 14,
  color: theme.palette.text.secondary,
  fontWeight: 500,
}));
